import type {
  AgentDefinition,
  Capability,
  CapabilityResult,
  Approval,
  ApprovalDecisionResult,
  Channel,
  ChannelSummary,
  Client,
  CreateMatterInput,
  Document,
  Lawyer,
  Matter,
  Message,
  SearchResult,
  WorkflowRun,
  WorkflowType,
  AgentSkill,
  KnowledgeResult,
  Invoice,
  InvoiceReview,
  UpdateInvoiceDraftInput,
  TimeEntry,
  UpdateTimeEntryInput,
  MiniApp,
  MentionableUser,
} from "./types";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...init,
    headers: init?.body instanceof FormData
      ? init.headers
      : { "Content-Type": "application/json", ...init?.headers },
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText })) as { error?: string };
    throw new ApiError(body.error ?? "The request could not be completed.", response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const api = {
  requestLoginCode: (email: string) =>
    request<{ challengeId: string; message: string }>("/auth/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  verifyLoginCode: (challengeId: string, code: string) =>
    request<{ lawyer: Lawyer }>("/auth/verify", {
      method: "POST",
      body: JSON.stringify({ challengeId, code }),
    }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
  session: () => request<{ lawyer: Lawyer }>("/session"),
  matters: () => request<{ matters: Matter[] }>("/matters"),
  clients: () => request<{ clients: Client[] }>("/clients"),
  createMatter: (input: CreateMatterInput) =>
    request<{ client: Client; matter: Matter; channel: Channel }>("/matters", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  channels: (matterId: string) => request<{ channels: Channel[] }>(`/matters/${matterId}/channels`),
  createChannel: (matterId: string, input: { name: string; description?: string; isPrivate?: boolean }) =>
    request<{ channel: Channel }>(`/matters/${matterId}/channels`, { method: "POST", body: JSON.stringify(input) }),
  deleteChannel: (channelId: string) =>
    request<void>(`/channels/${channelId}`, { method: "DELETE" }),
  messages: (channelId: string) => request<{ messages: Message[] }>(`/channels/${channelId}/messages?limit=100`),
  miniApps: (channelId: string) => request<{ miniApps: MiniApp[] }>(`/channels/${channelId}/mini-apps`),
  interactWithMiniApp: (
    appId: string,
    input: {
      expectedVersion: number;
      intent: "submit" | "complete" | "dismiss" | "toggle_item";
      actionId?: string;
      itemId?: string;
      values?: Record<string, string | number | boolean | null>;
    },
  ) => request<{ miniApp: MiniApp }>(`/mini-apps/${appId}/interactions`, { method: "POST", body: JSON.stringify(input) }),
  sendMessage: (channelId: string, input: { body: string; clientNonce: string; documentIds: string[]; mentionedLawyerIds: string[] }) =>
    request<{ message: Message }>(`/channels/${channelId}/messages`, { method: "POST", body: JSON.stringify(input) }),
  markRead: (channelId: string, messageId: string | null) =>
    request<void>(`/channels/${channelId}/read`, { method: "POST", body: JSON.stringify({ messageId }) }),
  agents: (channelId: string) => request<{ agents: AgentDefinition[] }>(`/channels/${channelId}/agents`),
  mentionableUsers: (channelId: string) =>
    request<{ users: MentionableUser[] }>(`/channels/${channelId}/mentionable-users`),
  summary: (channelId: string) => request<{ summary: ChannelSummary | null }>(`/channels/${channelId}/summary`),
  approvals: (channelId: string) => request<{ approvals: Approval[] }>(`/channels/${channelId}/approvals`),
  decideApproval: (channelId: string, approvalId: string, decision: "approve" | "reject") =>
    request<{ approval: ApprovalDecisionResult }>(`/channels/${channelId}/approvals/${approvalId}`, { method: "POST", body: JSON.stringify({ decision }) }),
  workflows: (matterId: string) => request<{ workflows: WorkflowRun[] }>(`/matters/${matterId}/workflows`),
  invoices: (matterId: string) => request<{ invoices: Invoice[] }>(`/matters/${matterId}/invoices`),
  timeEntries: (matterId: string) => request<{ timeEntries: TimeEntry[] }>(`/matters/${matterId}/time-entries`),
  updateTimeEntry: (timeEntryId: string, input: UpdateTimeEntryInput) =>
    request<{ timeEntry: TimeEntry }>(`/time-entries/${timeEntryId}`, { method: "PUT", body: JSON.stringify(input) }),
  deleteTimeEntry: (timeEntryId: string) =>
    request<{ timeEntry: TimeEntry }>(`/time-entries/${timeEntryId}`, { method: "DELETE" }),
  invoiceReview: (invoiceId: string) => request<InvoiceReview>(`/invoices/${invoiceId}`),
  updateInvoiceDraft: (invoiceId: string, input: UpdateInvoiceDraftInput) =>
    request<InvoiceReview>(`/invoices/${invoiceId}/draft`, { method: "PUT", body: JSON.stringify(input) }),
  markInvoiceSent: (invoiceId: string) =>
    request<{ invoice: Invoice; alreadySent: boolean }>(`/invoices/${invoiceId}/mark-sent`, { method: "POST" }),
  startWorkflow: (channelId: string, workflowType: WorkflowType) =>
    request<{ workflow: WorkflowRun }>(`/channels/${channelId}/workflows`, {
      method: "POST",
      body: JSON.stringify({ workflowType, input: {} }),
    }),
  decideWorkflow: (workflowRunId: string, decision: "approve" | "reject") =>
    request<{ ok: true }>(`/workflows/${workflowRunId}/decision`, {
      method: "POST",
      body: JSON.stringify({ decision }),
    }),
  workflowAction: (workflowRunId: string, action: "retry" | "cancel") =>
    request<{ workflow: WorkflowRun }>(`/workflows/${workflowRunId}/actions`, {
      method: "POST",
      body: JSON.stringify({ action }),
    }),
  documents: (matterId: string) => request<{ documents: Document[] }>(`/matters/${matterId}/documents`),
  uploadDocument: async (matterId: string, file: File, folderPath = "/") => {
    const form = new FormData();
    form.set("file", file);
    form.set("type", "Other");
    form.set("folderPath", folderPath);
    return request<{ document: Document }>(`/matters/${matterId}/documents`, { method: "POST", body: form });
  },
  moveDocument: (documentId: string, folderPath: string) =>
    request<{ document: Document }>(`/documents/${documentId}/folder`, {
      method: "PATCH",
      body: JSON.stringify({ folderPath }),
    }),
  deleteDocument: (documentId: string) =>
    request<{ document: Document }>(`/documents/${documentId}`, { method: "DELETE" }),
  search: (matterId: string, query: string) =>
    request<{ results: SearchResult[] }>(`/matters/${matterId}/search?q=${encodeURIComponent(query)}`),
  searchMatterKnowledge: (matterId: string, query: string) =>
    request<{ results: KnowledgeResult[] }>(`/matters/${matterId}/knowledge-search`, {
      method: "POST", body: JSON.stringify({ query }),
    }),
  searchPriorWorkProduct: (query: string, excludeMatterId?: string) =>
    request<{ results: KnowledgeResult[] }>("/knowledge-search/prior-work-product", {
      method: "POST", body: JSON.stringify({ query, excludeMatterId }),
    }),
  setPriorWorkProduct: (documentId: string, enabled: boolean) =>
    request<{ document: Document }>(`/documents/${documentId}/prior-work-product`, {
      method: "POST", body: JSON.stringify({ enabled }),
    }),
  agentSkills: () => request<{ skills: AgentSkill[] }>("/agent-skills"),
  agentSkill: (slug: string) => request<{ skill: AgentSkill }>(`/agent-skills/${encodeURIComponent(slug)}`),
  saveAgentSkill: (skill: Omit<AgentSkill, "updatedAt">) =>
    request<{ skill: AgentSkill }>("/agent-skills", { method: "POST", body: JSON.stringify(skill) }),
  capabilities: () => request<{ capabilities: Capability[] }>("/capabilities"),
  invokeCapability: (name: string, input: Record<string, unknown>, confirmed = false) =>
    request<CapabilityResult>(`/capabilities/${encodeURIComponent(name)}`, {
      method: "POST",
      body: JSON.stringify({ input, confirmed }),
    }),
  pushConfig: () => request<{ supported: boolean; publicKey: string | null }>("/push/config"),
  subscribePush: (subscription: PushSubscriptionJSON) =>
    request<{ ok: true }>("/push/subscriptions", { method: "POST", body: JSON.stringify(subscription) }),
  unsubscribePush: (endpoint: string) =>
    request<void>("/push/subscriptions", { method: "DELETE", body: JSON.stringify({ endpoint }) }),
};
